import { useState, useEffect } from "react";
import { useLocation, useNavigate, Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetTrigger } from "@/components/ui/sheet";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";
import { Menu, ChevronDown } from "lucide-react";
import pampLogo from "@/assets/pamp-logo.png";
import ThemeToggle from "@/components/ThemeToggle";

const sectorLinks = [
  { href: "/sectors/public-sector", label: "Public Sector" },
  { href: "/sectors/corporate-fm", label: "Corporate FM" },
  { href: "/cleaning-staff", label: "Cleaning & Facilities" },
];


const Header = () => {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 10);
    };

    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  const navigationLinks = [
    { href: "/", label: "Home" },
    { href: "/jobs", label: "Jobs" },
    { href: "/careers", label: "Careers" },
    { href: "/job-alerts", label: "Job Alerts" },
    { href: "/hire", label: "Hire Talent" },
    { href: "/employers", label: "For Employers" },
    { href: "/about", label: "About" },
    { href: "/faq", label: "FAQ" },
    { href: "/contact", label: "Contact" },
  ];

  const isActive = (href: string) => {
    if (href === "/") {
      return location.pathname === "/";
    }
    return location.pathname.startsWith(href);
  };

  return (
    <header className={`header-glow relative z-50 border-b border-border bg-card ${
      isScrolled ? "shadow-md" : "shadow-sm"
    }`}>


      {/* Skip Navigation Link for Keyboard Users */}
      <a 
        href="#main-content" 
        className="sr-only focus:not-sr-only focus:absolute focus:top-4 focus:left-4 focus:z-[60] focus:px-4 focus:py-2 focus:bg-primary focus:text-primary-foreground focus:rounded focus:outline-none"
      >
        Skip to main content
      </a>
      
      <div className="container mx-auto flex items-center justify-between px-6 py-4">
        <div className="flex items-center gap-3">
          <Link to="/" aria-label="PAMP Recruitment Home" className="block" style={{ aspectRatio: '1/1' }}>
            <img 
              src={pampLogo} 
              alt="PAMP Recruitment" 
              className="h-12 w-12 rounded-lg object-contain" 
              width="48" 
              height="48"
              loading="eager"
              fetchPriority="high"
            />
          </Link>
        </div>
        
        {/* Desktop Navigation */}
        <nav className="hidden lg:flex items-center gap-6 xl:gap-8" role="navigation" aria-label="Main navigation">
          {navigationLinks.map((link) => (
            <Link
              key={link.href}
              to={link.href}
              className={`relative text-sm xl:text-base transition-colors whitespace-nowrap ${
                isActive(link.href)
                  ? "text-primary font-medium after:absolute after:bottom-[-4px] after:left-0 after:w-full after:h-0.5 after:bg-primary"
                  : "text-foreground hover:text-primary"
              }`}
            >
              {link.label}
            </Link>
          ))}
          <DropdownMenu>
            <DropdownMenuTrigger
              className={`relative flex items-center gap-1 text-sm xl:text-base transition-colors whitespace-nowrap outline-none ${
                sectorLinks.some((s) => isActive(s.href))
                  ? "text-primary font-medium"
                  : "text-foreground hover:text-primary"
              }`}
            >
              Sectors <ChevronDown className="h-4 w-4" />
            </DropdownMenuTrigger>
            <DropdownMenuContent align="start" className="bg-popover z-50">
              {sectorLinks.map((s) => (
                <DropdownMenuItem key={s.href} asChild>
                  <Link to={s.href} className="cursor-pointer">{s.label}</Link>
                </DropdownMenuItem>
              ))}
            </DropdownMenuContent>
          </DropdownMenu>
        </nav>

        {/* Desktop CV Registration Button & Theme Toggle */}
        <div className="hidden lg:flex items-center gap-2">
          <ThemeToggle />
          <Button 
            className="bg-primary hover:bg-primary/90 text-primary-foreground"
            onClick={() => navigate('/cv-registration')}
            aria-label="Register your CV with PAMP Recruitment"
          >
            CV Registration
          </Button>
        </div>

        {/* Mobile & Tablet Menu */}
        <div className="flex lg:hidden items-center gap-2">
          <ThemeToggle />
          <Sheet open={mobileMenuOpen} onOpenChange={setMobileMenuOpen}>
            <SheetTrigger asChild>
              <Button
                variant="ghost"
                size="icon"
                aria-label="Open navigation menu"
                aria-expanded={mobileMenuOpen}
                aria-controls="mobile-menu"
              >
                <Menu className="h-6 w-6" />
              </Button>
            </SheetTrigger>
          <SheetContent side="right" id="mobile-menu" className="w-[300px] sm:w-[400px]">
            <SheetHeader>
              <SheetTitle>Menu</SheetTitle>
            </SheetHeader>
            <nav className="flex flex-col gap-4 mt-8" role="navigation" aria-label="Mobile navigation">
              {navigationLinks.map((link) => (
                <Link
                  key={link.href}
                  to={link.href}
                  className={`text-lg py-2 px-4 rounded-md transition-colors ${
                    isActive(link.href)
                      ? "text-primary font-medium bg-primary/10"
                      : "text-foreground hover:text-primary hover:bg-muted"
                  }`}
                  onClick={() => setMobileMenuOpen(false)}
                >
                  {link.label}
                </Link>
              ))}
              <div className="mt-2 px-4 text-sm font-semibold text-muted-foreground uppercase tracking-wide">Sectors</div>
              {sectorLinks.map((s) => (
                <Link
                  key={s.href}
                  to={s.href}
                  className={`text-base py-2 px-4 rounded-md transition-colors ${
                    isActive(s.href)
                      ? "text-primary font-medium bg-primary/10"
                      : "text-foreground hover:text-primary hover:bg-muted"
                  }`}
                  onClick={() => setMobileMenuOpen(false)}
                >
                  {s.label}
                </Link>
              ))}
              <Button 
                className="mt-4 bg-primary hover:bg-primary/90 text-primary-foreground w-full"
                onClick={() => {
                  setMobileMenuOpen(false);
                  navigate('/cv-registration');
                }}
                aria-label="Register your CV with PAMP Recruitment"
              >
                CV Registration
              </Button>
            </nav>
          </SheetContent>
        </Sheet>
        </div>
      </div>
    </header>
  );
};

export default Header;
